import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Snackbar, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import LockIcon from "@material-ui/icons/Lock";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import { useParams } from "react-router-dom";

import FormFieldRenderer from "../OfferCheckout/FormFieldRenderer";
import SupportContact from "../OfferCheckout/SupportContact";
import { validateAll } from "../OfferCheckout/utils/validate";
import { useOfferCheckoutStyles } from "../OfferCheckout/offerCheckout.styles";
import useOfferAnalyticsScripts from "../OfferCheckout/hooks/useOfferAnalyticsScripts";

import useWebinarConfig from "./hooks/useWebinarConfig";
import useWebinarTracking from "./useWebinarTracking";
import { useWebinarStyles } from "./webinarCheckout.styles";
import webinarApi from "../../services/webinarApi";
import AiSensyLogo from "../../static/AiSensyLogo.svg";
import { CHECKOUT_PREFIX } from "../../config/config";

/** Lazy-loads the Razorpay checkout script once. */
const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no window"));
    if (window.Razorpay) return resolve(window.Razorpay);
    const scriptId = "razorpay-checkout-script";
    const existing = document.getElementById(scriptId);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Razorpay));
      existing.addEventListener("error", () =>
        reject(new Error("Razorpay script failed to load"))
      );
      return;
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error("Razorpay script failed to load"));
    document.body.appendChild(script);
  });

const priceFmt = (n) => Number(n).toLocaleString("en-IN");
const pad2 = (n) => String(n).padStart(2, "0");

/** Returns {days,hours,mins,secs} until targetIso, or null once elapsed/invalid. */
const calcRemaining = (targetIso) => {
  if (!targetIso) return null;
  const diff = new Date(targetIso).getTime() - Date.now();
  if (Number.isNaN(diff) || diff <= 0) return null;
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    mins: Math.floor((diff % 3600000) / 60000),
    secs: Math.floor((diff % 60000) / 1000),
  };
};

/** Ticking countdown to a webinar's eventTimestamp (display-only urgency). */
const useCountdown = (targetIso) => {
  const [remaining, setRemaining] = useState(() => calcRemaining(targetIso));
  useEffect(() => {
    setRemaining(calcRemaining(targetIso));
    if (!targetIso) return undefined;
    const id = setInterval(() => setRemaining(calcRemaining(targetIso)), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  return remaining;
};

const WebinarCheckoutPage = () => {
  const { webinarSlug } = useParams();
  const classes = useOfferCheckoutStyles();
  const w = useWebinarStyles();

  useOfferAnalyticsScripts();
  const utmParams = useWebinarTracking(webinarSlug);
  const { webinar, loading, error, reload } = useWebinarConfig(webinarSlug);
  const countdown = useCountdown(webinar?.eventTimestamp);

  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [couponMessage, setCouponMessage] = useState("");

  // Payable shown on the CTA + sticky bar. Falls back to the plan price.
  const payable = couponApplied
    ? Math.max(0, Number(webinar?.price || 0) - discount)
    : Number(webinar?.price || 0);

  useEffect(() => {
    loadRazorpayScript().catch((err) =>
      console.warn("[WebinarCheckout] Razorpay preload failed:", err)
    );
  }, []);

  const setField = (key, v) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const showError = (msg) => setToast({ open: true, msg, severity: "error" });

  const handleCouponChange = (e) => {
    // Coupon codes: uppercase alphanumeric only (no spaces/special chars),
    // capped at 30 chars. Sanitize on input so paste/IME can't bypass it.
    const cleaned = (e.target.value || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 30);
    setCouponCode(cleaned);
    if (couponApplied) {
      // Editing the code invalidates the applied state until re-verified.
      setCouponApplied(false);
      setDiscount(0);
      setCouponMessage("");
    }
  };

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      setCouponMessage("Please enter a coupon code");
      return;
    }
    setValidatingCoupon(true);
    setCouponMessage("");
    try {
      const res = await webinarApi.verifyCoupon(
        code,
        Number(webinar.price || 0),
        webinar.slug
      );
      if (res && res.success && res.data) {
        const { discountType, discountValue } = res.data;
        const base = Number(webinar.price || 0);
        let d = 0;
        if (discountType === "percentage") {
          d = (base * Number(discountValue)) / 100;
        } else if (discountType === "fixed") {
          d = Number(discountValue);
        }
        d = Math.min(Math.max(0, Math.round(d * 100) / 100), base);
        setDiscount(d);
        setCouponApplied(true);
        setCouponMessage("Coupon applied!");
      } else {
        setCouponApplied(false);
        setDiscount(0);
        setCouponMessage((res && res.message) || "Invalid coupon code");
      }
    } catch (err) {
      setCouponApplied(false);
      setDiscount(0);
      setCouponMessage(err?.message || "Failed to verify coupon");
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleSubmit = async (ev) => {
    if (ev) ev.preventDefault();
    if (!webinar) return;

    const nextErrors = validateAll(webinar.formFields, values);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      const Razorpay = await loadRazorpayScript();

      // Server re-validates price from config — we never send the amount.
      const intent = await webinarApi.createPaymentIntent(
        webinar.slug,
        webinar,
        values,
        { dialCode: "+91", isoCode: "IN" },
        utmParams,
        couponApplied ? couponCode.trim() : null
      );

      if (intent.paymentGateway !== "razorpay") {
        throw new Error(`Unsupported payment gateway: ${intent.paymentGateway}`);
      }

      const utmQuery =
        Object.keys(utmParams).length > 0
          ? "?" + new URLSearchParams(utmParams).toString()
          : "";

      const razorpayOptions = {
        key: intent.keyId,
        amount: Math.round(Number(intent.amount) * 100),
        currency: intent.currency,
        name: "AISENSY COMMUNICATIONS PVT LTD",
        description: `Payment for ${webinar.productName}`,
        order_id: intent.intentId,
        prefill: {
          name: values.fullName,
          ...(values.email ? { email: values.email } : {}),
          contact: `+91${values.phone}`,
        },
        notes: intent.notes || {},
        theme: { color: "#04CF66" },
        retry: { enabled: true },
        remember_customer: true,
        modal: {
          backdropclose: false,
          escape: false,
          handleback: true,
          confirm_close: true,
          ondismiss: () => setSubmitting(false),
        },
        handler: function () {
          // Razorpay webhook is the authoritative writer; just redirect to
          // the webinar thank-you page (it tells the user the link is on its way).
          window.location.href = `${CHECKOUT_PREFIX}/thankyou-webinar${utmQuery}`;
        },
      };

      const rzp = new Razorpay(razorpayOptions);
      rzp.on("payment.failed", (response) => {
        const desc = response?.error?.description || "Payment failed";
        const reason = response?.error?.reason || "unknown";
        showError(`Payment failed: ${desc} (${reason})`);
        setSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      console.error("[WebinarCheckout] handleSubmit error:", err);
      if (err && err.errors && typeof err.errors === "object") setErrors(err.errors);
      showError(err?.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingRoot}>
        <img src={AiSensyLogo} alt="AiSensy" className={classes.loadingLogo} />
        <CircularProgress size={32} style={{ color: "#04cf66" }} />
      </div>
    );
  }

  if (error || !webinar) {
    const isNotFound = error && (error.status === 404 || error.status === 400);
    return (
      <div className={classes.errorRoot}>
        <div className={classes.errorCard}>
          <Typography className={classes.errorHeading}>
            {isNotFound ? "Webinar not available" : "We couldn't load this page"}
          </Typography>
          <Typography className={classes.errorMessage}>
            {isNotFound
              ? "This link may have expired or been taken down. Please check with whoever shared it."
              : error?.message || "Something went wrong while loading. Please retry."}
          </Typography>
          {!isNotFound && (
            <Button variant="contained" className={classes.retryButton} onClick={reload}>
              Retry
            </Button>
          )}
          <SupportContact compact />
        </div>
      </div>
    );
  }

  // Coupon block, defined once and rendered in two responsive slots:
  //   • desktop: inside the form, just above the CTA (wrapped `hideOnMobile`)
  //   • mobile: inside the fixed sticky bottom bar, above the Pay row
  const couponSection = (
    <div className={w.couponBox}>
      <div className={w.couponRow}>
        <input
          type="text"
          className={w.couponInput}
          placeholder="Have a coupon code?"
          value={couponCode}
          onChange={handleCouponChange}
          disabled={validatingCoupon}
          maxLength={30}
          autoCapitalize="characters"
          autoComplete="off"
        />
        <Button
          type="button"
          variant="outlined"
          className={w.couponButton}
          onClick={handleApplyCoupon}
          disabled={validatingCoupon || !couponCode.trim()}
        >
          {validatingCoupon ? "…" : couponApplied ? "Applied" : "Apply"}
        </Button>
      </div>
      {couponMessage ? (
        <div className={couponApplied ? w.couponMsgOk : w.couponMsgErr}>
          {couponApplied
            ? `${couponMessage} −${webinar.currency}${priceFmt(discount)}`
            : couponMessage}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className={classes.pageRoot}>
      {/* top sticky countdown banner */}
      <div className={w.topBanner}>
        <AccessTimeIcon className={w.topBannerIcon} />
        {countdown ? (
          <span>
            Hurry! Starts in{" "}
            <span className={w.topBannerTimer}>
              {countdown.days}d {pad2(countdown.hours)}h {pad2(countdown.mins)}m{" "}
              {pad2(countdown.secs)}s
            </span>
          </span>
        ) : (
          <span>Event has started — secure your spot now!</span>
        )}
      </div>

      <div className={classes.pageHeader}>
        <img src={AiSensyLogo} alt="AiSensy" className={classes.pageLogo} />
      </div>

      <div className={classes.pageContainer}>
        {/* lean order summary — confirm what you're registering for.
            Price lives in the sticky bar; the timer in the top banner; the
            marketing (host, agenda, social proof) is on the landing page. */}
        <div className={`${classes.planCard} ${w.cardCenter}`}>
          <div className={w.cardAccent} />

          <div className={classes.productName}>{webinar.productName}</div>

          {webinar.dateTime ? (
            <div className={w.cardDate}>
              <CalendarTodayIcon style={{ fontSize: 15 }} />
              {webinar.dateTime}
            </div>
          ) : null}
        </div>

        {/* lead form */}
        <form
          className={`${classes.formCard} ${w.formGap}`}
          onSubmit={handleSubmit}
          noValidate
        >
          <div className={classes.formTitle}>Reserve your spot</div>

          {(webinar.formFields || []).map((field) => (
            <FormFieldRenderer
              key={field.key}
              field={field}
              value={values[field.key]}
              error={errors[field.key]}
              onChange={setField}
            />
          ))}

          {/* Coupon (desktop) — on mobile it's rendered inside the sticky
              bottom bar instead (see below), per the responsive layout. */}
          {webinar.couponEnabled ? (
            <div className={w.hideOnMobile}>{couponSection}</div>
          ) : null}

          {/* Desktop CTA only — on mobile the sticky bottom bar carries the
              primary action, so this would be a redundant second button. */}
          <Button
            type="submit"
            variant="contained"
            className={`${classes.submitButton} ${w.hideOnMobile}`}
            disabled={submitting}
            startIcon={!submitting ? <LockIcon style={{ fontSize: 18 }} /> : undefined}
          >
            {submitting ? (
              <CircularProgress size={22} style={{ color: "#ffffff" }} />
            ) : couponApplied ? (
              `Pay ${webinar.currency}${priceFmt(payable)} Now`
            ) : (
              webinar.ctaText || `Pay ${webinar.currency}${priceFmt(webinar.price)} Now`
            )}
          </Button>
        </form>

        {/* Why join this masterclass — below the form */}
        {webinar.whyJoin ? (
          <div className={w.whyJoinCard}>
            {webinar.host ? (
              <div className={w.hostRow}>
                {webinar.host.avatarUrl ? (
                  <img
                    src={webinar.host.avatarUrl}
                    alt={webinar.host.name}
                    className={w.hostAvatar}
                  />
                ) : (
                  <div className={w.hostAvatarFallback}>
                    {(webinar.host.name || "?").charAt(0)}
                  </div>
                )}
                <div>
                  <div className={w.hostLabel}>Hosted by</div>
                  <div className={w.hostName}>{webinar.host.name}</div>
                  <div className={w.hostTitle}>{webinar.host.title}</div>
                </div>
              </div>
            ) : null}

            <div className={w.whyJoinHeader}>{webinar.whyJoin.header}</div>
            {webinar.whyJoin.sub ? (
              <div className={w.whyJoinSub}>{webinar.whyJoin.sub}</div>
            ) : null}
            {Array.isArray(webinar.whyJoin.bullets) ? (
              <ul className={classes.includesList}>
                {webinar.whyJoin.bullets.map((item, i) => (
                  <li key={i} className={classes.includeItem}>
                    <CheckCircleIcon className={classes.checkIcon} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {webinar.whyJoin.bonus ? (
              <div className={classes.bonus} style={{ marginTop: 16, marginBottom: 0 }}>
                <CheckCircleIcon style={{ fontSize: 18, color: "#236E34" }} />
                {webinar.whyJoin.bonus}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* spacer so the sticky bar never covers the last field on mobile */}
      <div
        className={w.stickySpacer}
        style={webinar.couponEnabled ? { height: 150 } : undefined}
      />

      {/* mobile sticky bottom bar (column: coupon row on top, then pay row) */}
      <div className={w.stickyBar}>
        {/* Coupon (mobile) — stuck to the bottom payment section. */}
        {webinar.couponEnabled ? couponSection : null}
        <div className={w.stickyPayRow}>
          <div className={w.stickyPriceWrap}>
            <div className={w.stickyPriceRow}>
              <span className={w.stickyPrice}>
                {webinar.currency}
                {priceFmt(payable)}
              </span>
              {couponApplied ? (
                // Coupon applied → show the original plan price struck through.
                <span className={w.stickyMrp}>
                  {webinar.currency}
                  {priceFmt(webinar.price)}
                </span>
              ) : webinar.mrp && webinar.mrp > webinar.price ? (
                <span className={w.stickyMrp}>
                  {webinar.currency}
                  {priceFmt(webinar.mrp)}
                </span>
              ) : null}
              <span className={w.stickyInclGst}>(incl GST)</span>
            </div>
            {webinar.urgencyText ? (
              <span className={w.stickyHint}>{webinar.urgencyText}</span>
            ) : webinar.dateTime ? (
              <span className={w.stickyHint}>{webinar.dateTime}</span>
            ) : null}
          </div>
          <Button
            variant="contained"
            className={w.stickyButton}
            disabled={submitting}
            onClick={handleSubmit}
            startIcon={!submitting ? <LockIcon style={{ fontSize: 16 }} /> : undefined}
          >
            {submitting ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Buy Now"}
          </Button>
        </div>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default WebinarCheckoutPage;
